// Persona government-ID verification step (2026-09-30).
// The embedded Persona SDK is a UX signal ONLY — verified status is always
// re-read from the server (api.personaStatus), never trusted from the browser.
import { useCallback, useEffect, useRef, useState } from "react";
import { BadgeCheck, Loader2, ShieldCheck, ShieldAlert, RotateCcw } from "lucide-react";
import { api } from "../lib/api";

const STATUS_META = {
  not_started: { label: "Verification required", cls: "text-amber-400" },
  created: { label: "In progress", cls: "text-blue-400" },
  pending: { label: "In progress", cls: "text-blue-400" },
  completed: { label: "Under review", cls: "text-blue-400" },
  needs_review: { label: "Under review", cls: "text-blue-400" },
  approved: { label: "Verified", cls: "text-emerald-400" },
  declined: { label: "Verification failed", cls: "text-red-400" },
  failed: { label: "Verification failed", cls: "text-red-400" },
  expired: { label: "Verification expired", cls: "text-amber-400" },
  cancelled: { label: "Cancelled", cls: "text-zinc-400" },
  unavailable: { label: "Temporarily unavailable", cls: "text-amber-400" },
  error: { label: "Something went wrong", cls: "text-red-400" },
};

export default function PersonaVerify({ onVerified, onClose }) {
  const [state, setState] = useState({ loading: true, enabled: false, status: "not_started", verified: false });
  const [busy, setBusy] = useState(false);
  const clientRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const s = await api.personaStatus();
      setState({ loading: false, ...s });
    } catch {
      setState({ loading: false, enabled: false, status: "unavailable", verified: false });
    }
  }, []);

  useEffect(() => {
    refresh();
    return () => { try { clientRef.current?.destroy(); } catch { /* noop */ } };
  }, [refresh]);

  const verified = state.verified;
  const notRequired = state.required === false; // admin master switch OFF — step auto-skips
  useEffect(() => { if (verified || notRequired) onVerified?.(); }, [verified, notRequired, onVerified]);

  const start = async () => {
    setBusy(true);
    try {
      const cfg = await api.personaConfig();
      if (!cfg.enabled) {
        setState((s) => ({ ...s, enabled: false, status: "unavailable" }));
        return;
      }
      const { Client } = await import("persona");
      const client = new Client({
        templateId: cfg.templateId,
        ...(cfg.environmentId ? { environmentId: cfg.environmentId } : {}),
        onReady: () => client.open(),
        onStart: async ({ inquiryId }) => { try { await api.personaBind(inquiryId); } catch { /* binding retried on status read */ } },
        onComplete: () => { try { client.destroy(); } catch { /* noop */ } refresh(); },
        onCancel: () => { try { client.destroy(); } catch { /* noop */ } setState((s) => ({ ...s, status: "cancelled" })); },
        onError: () => { try { client.destroy(); } catch { /* noop */ } setState((s) => ({ ...s, status: "error" })); },
      });
      clientRef.current = client;
    } catch {
      setState((s) => ({ ...s, status: "unavailable" }));
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div className="py-10 flex flex-col items-center gap-3" data-testid="persona-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF3B45]" />
        <p className="text-xs text-zinc-500">Checking verification status…</p>
      </div>
    );
  }

  const meta = STATUS_META[state.status] || STATUS_META.not_started;
  const canRetry = ["not_started", "declined", "failed", "expired", "cancelled", "error"].includes(state.status);

  return (
    <div data-testid="persona-verify">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-[#E50914]/15 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5 text-[#FF3B45]" />
        </span>
        <div>
          <p className="font-display font-bold text-lg">Verify your identity</p>
          <p className={`text-xs font-semibold ${meta.cls}`} data-testid="persona-status">{meta.label}</p>
        </div>
      </div>

      {state.status === "unavailable" ? (
        <div className="mt-4 p-4 rounded-xl border border-amber-500/25 bg-amber-500/5" data-testid="persona-unavailable">
          <p className="text-sm text-amber-300 flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
            {state.enabled === false
              ? "Identity verification is being rolled out and will be required soon. Bookings and publishing stay locked until verification launches — thank you for your patience."
              : "Identity verification is temporarily unavailable. Please try again in a few minutes — your booking has not been charged."}
          </p>
        </div>
      ) : notRequired ? (
        <div className="mt-4 p-4 rounded-xl border border-[#27272A] bg-white/[0.02]" data-testid="persona-not-required">
          <p className="text-sm text-zinc-300">Identity verification is currently not required — you can continue.</p>
        </div>
      ) : state.verified ? (
        <div className="mt-4 p-4 rounded-xl border border-emerald-500/25 bg-emerald-500/5" data-testid="persona-verified">
          <p className="text-sm text-emerald-300 flex items-center gap-2"><BadgeCheck className="w-4 h-4" /> Identity verified — you're all set.</p>
        </div>
      ) : (
        <p className="mt-4 text-sm text-zinc-400 leading-relaxed" data-testid="persona-explainer">
          Before your first booking, we verify your government-issued ID through Persona, a secure third-party
          verification provider. MyRedPark never sees or stores your ID document — only the verification result.
        </p>
      )}

      <div className="mt-6 flex flex-col gap-2">
        {canRetry && !state.verified && state.status !== "unavailable" && (
          <button type="button" onClick={start} disabled={busy} data-testid="persona-start-btn" className="btn-primary w-full text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            {state.status === "not_started" ? "Start verification" : "Retry verification"}
          </button>
        )}
        {["created", "pending", "completed", "needs_review"].includes(state.status) && (
          <button type="button" onClick={refresh} data-testid="persona-refresh-btn" className="btn-secondary w-full text-sm">
            Refresh status
          </button>
        )}
        {onClose && (
          <button type="button" onClick={onClose} data-testid="persona-close-btn" className="text-xs text-zinc-500 hover:text-white transition-colors py-1">
            Back to booking
          </button>
        )}
      </div>
    </div>
  );
}
